import { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';

export default function FunnelFilters({
    searchQuery,
    onSearchChange,
    platformFilter,
    onPlatformChange,
    statusFilter,
    onStatusChange,
    sortBy,
    onSortChange,
    totalCount,
    filteredCount
}) {
    const [showSortDropdown, setShowSortDropdown] = useState(false);
    const [showPlatformDropdown, setShowPlatformDropdown] = useState(false);
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const sortRef = useRef(null);
    const platformRef = useRef(null);
    const statusRef = useRef(null);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (sortRef.current && !sortRef.current.contains(e.target)) {
                setShowSortDropdown(false);
            }
            if (platformRef.current && !platformRef.current.contains(e.target)) {
                setShowPlatformDropdown(false);
            }
            if (statusRef.current && !statusRef.current.contains(e.target)) {
                setShowStatusDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const sortOptions = [
        { value: 'recent', label: 'Most Recent' },
        { value: 'oldest', label: 'Oldest First' },
        { value: 'alphabetical', label: 'Alphabetical' }
    ];

    const platformOptions = [
        { value: 'all', label: 'All Platforms' },
        { value: 'meta', label: 'Meta' },
        { value: 'google', label: 'Google' },
        { value: 'tiktok', label: 'TikTok' },
        { value: 'linkedin', label: 'LinkedIn' }
    ];

    const statusOptions = [
        { value: 'all', label: 'All Status' },
        { value: 'draft', label: 'Draft' },
        { value: 'pending', label: 'Pending' }
    ];

    const currentSort = sortOptions.find(o => o.value === sortBy) || sortOptions[0];
    const currentPlatform = platformOptions.find(o => o.value === platformFilter) || platformOptions[0];
    const currentStatus = statusOptions.find(o => o.value === statusFilter) || statusOptions[0];

    const hasActiveFilters = platformFilter !== 'all' || statusFilter !== 'all' || searchQuery;

    const clearAllFilters = () => {
        onSearchChange('');
        onPlatformChange('all');
        onStatusChange('all');
    };

    const dropdownStyles = {
        position: 'absolute',
        top: '100%',
        left: 0,
        marginTop: 'var(--space-1)',
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: 100,
        minWidth: '150px'
    };

    const buttonStyles = {
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-2) var(--space-3)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        backgroundColor: 'var(--color-surface)',
        color: 'var(--color-text-primary)',
        cursor: 'pointer',
        fontSize: '0.875rem',
        whiteSpace: 'nowrap'
    };

    const renderDropdown = (options, currentValue, onChange, setShowDropdown) => (
        <div style={dropdownStyles}>
            {options.map(option => (
                <button
                    key={option.value}
                    onClick={() => {
                        onChange(option.value);
                        setShowDropdown(false);
                    }}
                    style={{
                        display: 'block',
                        width: '100%',
                        padding: 'var(--space-2) var(--space-3)',
                        border: 'none',
                        backgroundColor: currentValue === option.value ? 'var(--primary-50)' : 'transparent',
                        color: currentValue === option.value ? 'var(--primary-700)' : 'var(--color-text-primary)',
                        cursor: 'pointer',
                        textAlign: 'left',
                        fontSize: '0.875rem'
                    }}
                >
                    {option.label}
                </button>
            ))}
        </div>
    );

    return (
        <div style={{ marginBottom: 'var(--space-4)' }}>
            {/* Main filter bar */}
            <div style={{
                display: 'flex',
                gap: 'var(--space-3)',
                alignItems: 'center',
                flexWrap: 'wrap'
            }}>
                {/* Search input */}
                <div style={{
                    position: 'relative',
                    flex: '1 1 250px',
                    minWidth: '200px',
                    maxWidth: '400px'
                }}>
                    <Search
                        size={18}
                        style={{
                            position: 'absolute',
                            left: 'var(--space-3)',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: 'var(--color-text-tertiary)'
                        }}
                    />
                    <input
                        type="text"
                        placeholder="Search funnels..."
                        value={searchQuery}
                        onChange={(e) => onSearchChange(e.target.value)}
                        style={{
                            width: '100%',
                            padding: 'var(--space-2) var(--space-3) var(--space-2) var(--space-8)',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-md)',
                            backgroundColor: 'var(--color-surface)',
                            color: 'var(--color-text-primary)',
                            fontSize: '0.875rem'
                        }}
                    />
                    {searchQuery && (
                        <button
                            onClick={() => onSearchChange('')}
                            style={{
                                position: 'absolute',
                                right: 'var(--space-2)',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 'var(--space-1)',
                                color: 'var(--color-text-tertiary)'
                            }}
                        >
                            <X size={14} />
                        </button>
                    )}
                </div>

                {/* Platform dropdown */}
                <div ref={platformRef} style={{ position: 'relative' }}>
                    <button
                        onClick={() => setShowPlatformDropdown(!showPlatformDropdown)}
                        style={{
                            ...buttonStyles,
                            backgroundColor: platformFilter !== 'all' ? 'var(--primary-50)' : 'var(--color-surface)',
                            borderColor: platformFilter !== 'all' ? 'var(--color-primary)' : 'var(--color-border)'
                        }}
                    >
                        <span>{currentPlatform.label}</span>
                        <ChevronDown size={14} />
                    </button>
                    {showPlatformDropdown && renderDropdown(platformOptions, platformFilter, onPlatformChange, setShowPlatformDropdown)}
                </div>

                {/* Status dropdown */}
                <div ref={statusRef} style={{ position: 'relative' }}>
                    <button
                        onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                        style={{
                            ...buttonStyles,
                            backgroundColor: statusFilter !== 'all' ? 'var(--primary-50)' : 'var(--color-surface)',
                            borderColor: statusFilter !== 'all' ? 'var(--color-primary)' : 'var(--color-border)'
                        }}
                    >
                        <span>{currentStatus.label}</span>
                        <ChevronDown size={14} />
                    </button>
                    {showStatusDropdown && renderDropdown(statusOptions, statusFilter, onStatusChange, setShowStatusDropdown)}
                </div>

                {/* Sort dropdown */}
                <div ref={sortRef} style={{ position: 'relative' }}>
                    <button
                        onClick={() => setShowSortDropdown(!showSortDropdown)}
                        style={buttonStyles}
                    >
                        <span>{currentSort.label}</span>
                        <ChevronDown size={14} />
                    </button>
                    {showSortDropdown && renderDropdown(sortOptions, sortBy, onSortChange, setShowSortDropdown)}
                </div>

                {/* Clear filters */}
                {hasActiveFilters && (
                    <button
                        onClick={clearAllFilters}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-1)',
                            padding: 'var(--space-2) var(--space-3)',
                            background: 'none',
                            border: 'none',
                            color: 'var(--color-primary)',
                            cursor: 'pointer',
                            fontSize: '0.875rem'
                        }}
                    >
                        <X size={14} />
                        Clear filters
                    </button>
                )}
            </div>

            {/* Results count */}
            <div style={{
                marginTop: 'var(--space-2)',
                fontSize: '0.875rem',
                color: 'var(--color-text-tertiary)'
            }}>
                {filteredCount === totalCount
                    ? `${totalCount} funnel${totalCount !== 1 ? 's' : ''}`
                    : `${filteredCount} of ${totalCount} funnels`
                }
            </div>
        </div>
    );
}
